import { TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CollarColor } from '@/modules/events/types';

export const useCollarTab = () => {
  const [activeFocusFieldId, setActiveFocusFieldId] = useState<string>('');

  const { control, watch } = useFormContext();

  const fields: CollarColor[] = watch('collarColors') ?? [];

  const { append, remove } = useFieldArray({
    control,
    name: 'collarColors',
  });

  const onAdd = () => {
    append({ name: '', hex: '' });
  };

  const columns: ColumnDef<CollarColor>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const fieldName = `collarColors.${row.index}.name`;

        return (
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <Input
                  {...field}
                  autoFocus={fieldName === activeFocusFieldId}
                  onFocus={() => setActiveFocusFieldId(fieldName)}
                  onBlur={() => setActiveFocusFieldId('')}
                  placeholder="Enter name"
                  className="w-[160px] border-0"
                />
                <FormMessage />
              </FormItem>
            )}
          />
        );
      },
    },
    {
      size: 220,
      accessorKey: 'hex',
      header: 'Hex Color',
      cell: ({ row }) => {
        const fieldName = `collarColors.${row.index}.hex`;

        return (
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => {
              const isValidHex = /^#[0-9a-fA-F]{6}$/.test(field.value);

              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-sm border border-zinc-700 flex-shrink-0"
                      style={{
                        backgroundColor: isValidHex
                          ? field.value
                          : 'transparent',
                      }}
                    />
                    <Input
                      {...field}
                      autoFocus={fieldName === activeFocusFieldId}
                      onFocus={() => setActiveFocusFieldId(fieldName)}
                      onBlur={() => setActiveFocusFieldId('')}
                      placeholder="#000000"
                      className="w-[120px] border-0"
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        );
      },
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          data-testid="remove-collar-button"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  return {
    columns,
    fields,
    onAdd,
  };
};
